import React, { useMemo, useState } from 'react';

import H from '@zola/zola-ui/src/typography/Headings';

import { setCookie, setLocalStorage } from '@zola-helpers/client/dist/es/util/storage';
import { ALL_THEME_MOTIFS, ThemeTileProps } from './themeTiles';

import {
  Container,
  PhotoLinkWrapper,
  StyledButton,
  StyledImage,
  StyledLink,
  StyledSkeletonLoader,
  ThemeMotifChips,
  ThemeMotifChip,
  ThemeTiles,
  TileWrapper,
  TitleContainer,
} from './ThemeSelector.styles';

type ThemeTileComponentProps = {
  hideOnMobile: boolean;
  loadImages: boolean;
  themeTile: ThemeTileProps;
};

const ThemeTile: React.FC<ThemeTileComponentProps> = ({ hideOnMobile, loadImages, themeTile }) => {
  // Needed to load set of images more smoothly
  const [isLoadingImage, setIsLoadingImage] = useState(loadImages);

  const { href, imgAlt, imgSrc, previewIdentifier } = themeTile;
  const { layout, themeKey } = previewIdentifier;

  const goToOnboardingWithPreview = (e: React.MouseEvent<Element>) => {
    e.preventDefault();

    // To come back to WW LP if user exits onboarding
    setLocalStorage('websiteQuickOnboardPreviousLocation', window.location.href);

    // To inform which theme to show on the onboarding live preview
    setCookie('preauthSelectedWeddingTheme', themeKey, 1800);
    setCookie('preauthSelectedWeddingThemeKey', themeKey, 1800);
    setCookie('preauthSelectedLayout', layout, 1800);

    window.location.assign('/wedding/onboard/wedding-website/form');
  };

  return (
    <TileWrapper hideOnMobile={hideOnMobile}>
      <PhotoLinkWrapper href={href} onClick={goToOnboardingWithPreview}>
        {isLoadingImage ? <StyledSkeletonLoader /> : null}
        <StyledImage src={imgSrc} alt={imgAlt} onLoad={() => setIsLoadingImage(false)} />
      </PhotoLinkWrapper>
      <StyledLink arrow href={href} noUnderline noTextTransform onClick={goToOnboardingWithPreview}>
        Choose this design
      </StyledLink>
    </TileWrapper>
  );
};

const ThemeSelector: React.FC = () => {
  const [loadImages, setLoadImages] = useState(false);
  const [selectedThemeMotifKey, setSelectedThemeMotifKey] = useState<string>('mostPopular');
  const selectedThemeMotif = useMemo(
    () => ALL_THEME_MOTIFS.filter(themeMotif => themeMotif.key === selectedThemeMotifKey)?.[0],
    [selectedThemeMotifKey]
  );
  const { themeMotifHref, tiles } = selectedThemeMotif;

  return (
    <Container>
      <TitleContainer>
        <H.TitleMedium3>Create a wedding website from our templates</H.TitleMedium3>
      </TitleContainer>
      <ThemeMotifChips>
        {ALL_THEME_MOTIFS.map(i => (
          <ThemeMotifChip
            active={selectedThemeMotifKey === i.key}
            key={`theme-motif-chip-${i.key}`}
            large
            onClick={() => {
              setLoadImages(true);
              setSelectedThemeMotifKey(i.key);
            }}
          >
            {i.label}
          </ThemeMotifChip>
        ))}
      </ThemeMotifChips>
      <ThemeTiles>
        {tiles.map((tile, index) => (
          <ThemeTile
            hideOnMobile={index > 2}
            key={`theme-tile-${index}`}
            loadImages={loadImages}
            themeTile={tile}
          />
        ))}
      </ThemeTiles>
      <StyledButton component="a" href={themeMotifHref} mobileSize="large" tabletSize="large">
        Explore all designs
      </StyledButton>
    </Container>
  );
};

export default ThemeSelector;
